import { api, type ApiError, type AuthUser } from '../api/client.js';

/**
 * Renders the "set a new password" gate that every freshly invited
 * user is locked into until they pick a real password. The backend
 * enforces the same gate: any /api/* call other than /auth/{me,
 * logout,change-password} returns 403 PASSWORD_CHANGE_REQUIRED while
 * `must_change_password=true`.
 *
 * @param onSuccess Called after the new password is accepted; the
 * bootstrap then calls /auth/me again and routes to the components
 * list.
 */
export function renderChangePasswordPage(
  root: HTMLElement,
  user: AuthUser,
  onSuccess: () => void,
  onSignOut: () => void,
): void {
  root.innerHTML = `
    <div class="login-shell">
      <sl-card class="login-card">
        <div slot="header">
          <strong>Set a new password</strong>
        </div>
        <p style="margin: 0 0 0.75rem; color: var(--sl-color-neutral-700); font-size: 0.875rem">
          You're signed in with a temporary password. Pick a new password to
          continue. The rest of the app is locked until you do.
        </p>
        <form class="field-stack" id="cp-form" autocomplete="off">
          <sl-input
            id="cp-email"
            label="Account"
            value="${escapeAttr(user.email ?? user.id)}"
            readonly></sl-input>
          <sl-input
            id="cp-current"
            type="password"
            label="Current temporary password"
            password-toggle
            autocomplete="current-password"
            required></sl-input>
          <sl-input
            id="cp-new"
            type="password"
            label="New password"
            help-text="At least 8 characters; must differ from the temporary one."
            password-toggle
            minlength="8"
            autocomplete="new-password"
            required></sl-input>
          <sl-input
            id="cp-confirm"
            type="password"
            label="Confirm new password"
            password-toggle
            minlength="8"
            autocomplete="new-password"
            required></sl-input>
          <sl-alert id="cp-error" variant="danger" closable></sl-alert>
          <div style="display:flex; gap:0.5rem; justify-content:flex-end">
            <sl-button id="cp-signout" variant="default">Sign out</sl-button>
            <sl-button id="cp-submit" type="submit" variant="primary">Save password</sl-button>
          </div>
        </form>
      </sl-card>
    </div>
  `;

  const form = root.querySelector<HTMLFormElement>('#cp-form')!;
  const errorEl = root.querySelector<HTMLElement & { open: boolean }>('#cp-error')!;
  const currentEl = root.querySelector<HTMLInputElement>('#cp-current')!;
  const newEl = root.querySelector<HTMLInputElement>('#cp-new')!;
  const confirmEl = root.querySelector<HTMLInputElement>('#cp-confirm')!;
  const submitBtn = root.querySelector<HTMLElement>('#cp-submit')!;
  const signoutBtn = root.querySelector<HTMLElement>('#cp-signout')!;

  const showError = (msg: string) => {
    errorEl.textContent = msg;
    errorEl.open = true;
  };
  const hideError = () => {
    errorEl.open = false;
  };

  // Same anti-autofill pattern as the login page: browsers happily ignore
  // autocomplete="new-password" and inject saved credentials anyway.
  const clear = () => {
    currentEl.value = '';
    newEl.value = '';
    confirmEl.value = '';
  };
  requestAnimationFrame(clear);
  setTimeout(clear, 100);

  signoutBtn.addEventListener('click', async () => {
    await api.logout().catch(() => undefined);
    onSignOut();
  });

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    hideError();

    const currentPassword = currentEl.value;
    const newPassword = newEl.value;
    const confirmPassword = confirmEl.value;

    if (newPassword.length < 8) {
      showError('New password must be at least 8 characters');
      return;
    }
    if (newPassword === currentPassword) {
      showError('New password must be different from the temporary password');
      return;
    }
    if (newPassword !== confirmPassword) {
      showError('Confirmation does not match');
      return;
    }

    const loadingBtn = submitBtn as HTMLButtonElement & { loading: boolean };
    loadingBtn.loading = true;
    try {
      await api.changePassword({ currentPassword, newPassword });
      onSuccess();
    } catch (err) {
      const apiErr = err as ApiError;
      showError(apiErr?.message ?? 'Failed to change password');
    } finally {
      loadingBtn.loading = false;
    }
  });
}

function escapeAttr(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
